/**
 * App Component - Main application router
 * Configures all routes with authentication and permissions
 */

import { Routes, Route, Navigate, useParams } from 'react-router-dom';
import { lazy, Suspense, useEffect } from 'react';
import styled from 'styled-components';
import { MainLayout } from './components/layout';
import { KnowledgeCenterLayout } from './components/support/help/KnowledgeCenterLayout';
import { EnvironmentBanner } from './components/layout';
import { ProtectedRoute } from './components/auth';
import { LoginPage } from './pages/LoginPage';
import { DashboardPage } from './pages/DashboardPage';
import { UserManagementPage } from './pages/UserManagementPage';
import { TeamPage } from './pages/TeamPage';
import { RoleManagementPage } from './pages/RoleManagementPage';
import { PermissionManagementPage } from './pages/PermissionManagementPage';
import { UnauthorizedPage } from './pages/UnauthorizedPage';
import { PlanManagementPage } from './pages/PlanManagementPage';
import {
  SettingsPage,
  RoleDetailPage,
  AcceptInvitePage,
  OnboardingPage,
  MyTasksPage,
  TaskSchedulerPage,
  LocationManagementPage,
  ClientManagementPage,
  GoalsManagementPage,
} from './pages';
import { useAuthStore } from './stores/authStore';
import { useInactivityTimeout } from './hooks/useInactivityTimeout';
import { ROUTES } from './routes/paths';

// Platform Admin Pages (lazy loaded)
const PlatformDashboardPage = lazy(() => import('./pages/PlatformDashboardPage/PlatformDashboardPage'));
const PlatformOrgsPage = lazy(() => import('./pages/PlatformOrgsPage/PlatformOrgsPage'));
const PlatformOrgDetailPage = lazy(() => import('./pages/PlatformOrgDetailPage/PlatformOrgDetailPage'));
const RegistrationRequestsPage = lazy(() => import('./pages/RegistrationRequestsPage/RegistrationRequestsPage'));
const PlatformActionCenterPage = lazy(() => import('./pages/PlatformActionCenterPage/PlatformActionCenterPage'));
const PlatformUserLookupPage = lazy(() => import('./pages/PlatformUserLookupPage/PlatformUserLookupPage'));
const PlatformUserDetailPage = lazy(() => import('./pages/PlatformUserDetailPage/PlatformUserDetailPage'));
const PaidSeatUsagePage = lazy(() => import('./pages/PaidSeatUsagePage/PaidSeatUsagePage'));
const EmailMonitoringPage = lazy(() => import('./pages/EmailMonitoringPage/EmailMonitoringPage'));
const SubscriptionManagementPage = lazy(() => import('./pages/SubscriptionManagementPage/SubscriptionManagementPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage/InvoicesPage'));
const FileAssetsPage = lazy(() => import('./pages/FileAssetsPage/FileAssetsPage'));
const ResourcesPage = lazy(() => import('./pages/ResourcesPage/ResourcesPage'));

// Help Center — full-page article reading experience (lazy loaded, no permission gate)
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage/HelpCenterPage'));
const HelpArticlePage = lazy(() => import('./pages/HelpCenterPage/HelpArticlePage'));
const ReleaseNotesPage = lazy(() => import('./pages/HelpCenterPage/ReleaseNotesPage'));
const CommonQuestionsPage = lazy(() => import('./pages/HelpCenterPage/CommonQuestionsPage'));

// Red Dot v2 + Preference Assessment clinical workflows (lazy loaded)
const WorkflowHomePage = lazy(() => import('./pages/WorkflowHomePage/WorkflowHomePage'));
const ActionCenterPage = lazy(() => import('./pages/ActionCenterPage/ActionCenterPage'));
const RedDotFormPage = lazy(() => import('./pages/RedDotFormPage/RedDotFormPage'));
const RedDotDetailPage = lazy(() => import('./pages/RedDotDetailPage/RedDotDetailPage'));
const PAHomePage = lazy(() => import('./pages/PreferenceAssessmentPage/PAHomePage'));
const PACdssPage = lazy(() => import('./pages/PreferenceAssessmentPage/PACdssPage'));
const PADetailPage = lazy(() => import('./pages/PreferenceAssessmentPage/PADetailPage'));

// Analytics (Red Dot Insights dashboard — lazy loaded)
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage/AnalyticsPage'));

// Entra auth flow pages (lazy loaded, public)
const AuthCallbackPage = lazy(() => import('./pages/AuthCallbackPage/AuthCallbackPage'));
const OrgSelectPage = lazy(() => import('./pages/OrgSelectPage/OrgSelectPage'));

/** Wraps a lazy-loaded platform admin page with super-admin guard + Suspense. */
function PlatformRoute({ children }: { children: React.ReactNode }) {
  return (
    <ProtectedRoute requireSuperAdmin>
      <Suspense fallback={null}>{children}</Suspense>
    </ProtectedRoute>
  );
}

/** Session expiration warning modal (HIPAA inactivity timeout) */
function SessionWarningModal({
  remainingSeconds,
  onDismiss,
}: {
  remainingSeconds: number;
  onDismiss: () => void;
}) {
  const minutes = Math.floor(remainingSeconds / 60);
  const seconds = remainingSeconds % 60;

  return (
    <ModalOverlay>
      <ModalContent>
        <ModalTitle>Session Expiring</ModalTitle>
        <ModalText>
          Your session will expire in{' '}
          <strong>
            {minutes}:{seconds.toString().padStart(2, '0')}
          </strong>{' '}
          due to inactivity.
        </ModalText>
        <ModalText>Click below to continue your session.</ModalText>
        <ModalButton onClick={onDismiss}>Continue Session</ModalButton>
      </ModalContent>
    </ModalOverlay>
  );
}

/**
 * Redirects a legacy support-queue detail path to its Action Center equivalent,
 * carrying the request id across. Notification `linkUrl`s already persisted in
 * the DB (and links in emails already delivered) point at the old paths, so
 * these must keep resolving indefinitely, not just through one release.
 */
function LegacySupportRedirect({ to }: { to: string }) {
  const { id } = useParams<{ id?: string }>();
  return <Navigate to={id ? `${to}/${id}` : to} replace />;
}

function App() {
  const { isAuthenticated, user, refreshUser, sessionTimeoutMinutes } = useAuthStore();
  const { showWarning, remainingSeconds, dismissWarning } = useInactivityTimeout(sessionTimeoutMinutes);
  /* Show banner on login/public pages. MainLayout handles it for authenticated pages. */
  const showRootBanner = !isAuthenticated;
  const needsOnboarding =
    isAuthenticated && user?.onboardingComplete === false && !user?.isSuperAdmin;
  const defaultRedirect = needsOnboarding
    ? ROUTES.ONBOARDING
    : user?.isSuperAdmin
      ? ROUTES.PLATFORM
      : ROUTES.DASHBOARD;

  // Hydrate entitlements immediately when authenticated (fresh login or page reload),
  // not only on the first window focus — otherwise plan-gated UI (dashboard cards,
  // role editor restrictions) can briefly show stale/open-gate state right after login.
  // Also refresh on window focus so license changes propagate without a full re-login.
  // refreshUser guards against unauthenticated calls internally.
  useEffect(() => {
    if (isAuthenticated) {
      refreshUser();
    }
    window.addEventListener('focus', refreshUser);
    return () => window.removeEventListener('focus', refreshUser);
  }, [refreshUser, isAuthenticated]);

  return (
    <>
      {showRootBanner && <EnvironmentBanner />}
      {isAuthenticated && showWarning && (
        <SessionWarningModal remainingSeconds={remainingSeconds} onDismiss={dismissWarning} />
      )}
      <Routes>
        {/* Public Routes */}
        <Route
          path="/login"
          element={isAuthenticated ? <Navigate to={defaultRedirect} replace /> : <LoginPage />}
        />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />

        {/* Entra auth callback routes (Public) */}
        <Route path="/auth/callback" element={<Suspense fallback={null}><AuthCallbackPage /></Suspense>} />
        <Route path="/auth/org-select" element={<Suspense fallback={null}><OrgSelectPage /></Suspense>} />

        {/* Onboarding (Authenticated but no org yet) */}
        <Route
          path="/onboarding"
          element={
            !isAuthenticated ? (
              <Navigate to={ROUTES.LOGIN} replace />
            ) : !needsOnboarding ? (
              <Navigate to={ROUTES.DASHBOARD} replace />
            ) : (
              <OnboardingPage />
            )
          }
        />

        {/* Accept Invitation (Public - No Auth Required) */}
        <Route path="/invite/:token" element={<AcceptInvitePage />} />

        {/* Knowledge Center — auth-required but rendered WITHOUT the app
            Header/Sidebar (opens in its own tab from the support widget;
            standalone reading chrome). No permission gate: every role. */}
        <Route
          element={
            <ProtectedRoute>
              <KnowledgeCenterLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path="/help"
            element={
              <Suspense fallback={null}>
                <HelpCenterPage />
              </Suspense>
            }
          />
          {/* Literal segment must precede the :articleId param so
              /help/release-notes is never read as an article id. */}
          <Route
            path="/help/release-notes"
            element={
              <Suspense fallback={null}>
                <ReleaseNotesPage />
              </Suspense>
            }
          />
          <Route
            path="/help/common-questions"
            element={
              <Suspense fallback={null}>
                <CommonQuestionsPage />
              </Suspense>
            }
          />
          <Route
            path="/help/:articleId"
            element={
              <Suspense fallback={null}>
                <HelpArticlePage />
              </Suspense>
            }
          />
        </Route>

        {/* Protected Routes with Layout */}
        <Route
          element={
            <ProtectedRoute>
              <MainLayout />
            </ProtectedRoute>
          }
        >
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route
            path="/users"
            element={
              <ProtectedRoute requiredPermissions={['team:manage']}>
                <UserManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/team"
            element={
              <ProtectedRoute requiredPermissions={['team:manage']}>
                <TeamPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/organizations"
            element={<Navigate to="/platform/organizations" replace />}
          />
          <Route
            path="/roles"
            element={
              <ProtectedRoute requiredPermissions={['role:extend', 'roles:manage']}>
                <RoleManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles/:id"
            element={
              <ProtectedRoute requiredPermissions={['role:extend', 'roles:manage']}>
                <RoleDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/permissions"
            element={
              <ProtectedRoute requiredPermissions={['permissions:manage']}>
                <PermissionManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/settings"
            element={
              <ProtectedRoute requiredPermissions={['profile:manage']}>
                <SettingsPage />
              </ProtectedRoute>
            }
          />
          {/* Plan Management (Super Admin Only) */}
          <Route
            path="/admin/plans"
            element={
              <ProtectedRoute requireSuperAdmin>
                <PlanManagementPage />
              </ProtectedRoute>
            }
          />
          {/* File Assets Management (Super Admin Only) */}
          <Route
            path="/admin/file-assets"
            element={<PlatformRoute><FileAssetsPage /></PlatformRoute>}
          />
          {/* Resource Registry Management (Super Admin Only) */}
          <Route
            path="/admin/resources"
            element={<PlatformRoute><ResourcesPage /></PlatformRoute>}
          />

          {/* Platform Admin Routes */}
          <Route path="/platform" element={<PlatformRoute><PlatformDashboardPage /></PlatformRoute>} />
          <Route path="/platform/organizations" element={<PlatformRoute><PlatformOrgsPage /></PlatformRoute>} />
          <Route path="/platform/organizations/:id" element={<PlatformRoute><PlatformOrgDetailPage /></PlatformRoute>} />
          <Route path="/platform/registration-requests" element={<PlatformRoute><RegistrationRequestsPage /></PlatformRoute>} />
          <Route path="/platform/action-center" element={<PlatformRoute><PlatformActionCenterPage /></PlatformRoute>} />
          <Route path="/platform/action-center/support/:id" element={<PlatformRoute><PlatformActionCenterPage /></PlatformRoute>} />
          {/* Legacy platform support paths — the queue moved into the platform
              Action Center. Kept as redirects so deep links in already-sent
              emails and stored notifications still resolve. */}
          <Route path="/platform/support" element={<Navigate to="/platform/action-center" replace />} />
          <Route path="/platform/support/:id" element={<LegacySupportRedirect to="/platform/action-center/support" />} />
          <Route path="/platform/users" element={<PlatformRoute><PlatformUserLookupPage /></PlatformRoute>} />
          <Route path="/platform/users/:id" element={<PlatformRoute><PlatformUserDetailPage /></PlatformRoute>} />
          <Route path="/platform/subscriptions" element={<PlatformRoute><SubscriptionManagementPage /></PlatformRoute>} />
          <Route path="/platform/invoices" element={<PlatformRoute><InvoicesPage /></PlatformRoute>} />
          <Route path="/platform/billing/seat-usage" element={<PlatformRoute><PaidSeatUsagePage /></PlatformRoute>} />
          <Route path="/platform/email-logs" element={<PlatformRoute><EmailMonitoringPage /></PlatformRoute>} />

          {/* My Tasks — personal task inbox */}
          <Route
            path="/my-tasks"
            element={
              <ProtectedRoute requiredPermissions={['tasks:my_tasks']}>
                <MyTasksPage />
              </ProtectedRoute>
            }
          />
          {/* Task Scheduler — supervisor/admin task assignment view */}
          <Route
            path="/tasks"
            element={
              <ProtectedRoute requiredPermissions={['tasks:my_tasks']}>
                <TaskSchedulerPage />
              </ProtectedRoute>
            }
          />
          {/* Center / Location Management */}
          <Route
            path="/locations"
            element={
              <ProtectedRoute requiredPermissions={['locations:manage']}>
                <LocationManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/locations/:id"
            element={
              <ProtectedRoute requiredPermissions={['locations:manage']}>
                <LocationManagementPage />
              </ProtectedRoute>
            }
          />
          {/* Goals Management */}
          <Route
            path="/goals"
            element={
              <ProtectedRoute requiredPermissions={['goals:manage']}>
                <GoalsManagementPage />
              </ProtectedRoute>
            }
          />
          {/* Red Dot Workflow (v2) */}
          <Route
            path="/red-dot"
            element={
              <ProtectedRoute requireWorkflow="red_dot">
                <Suspense fallback={null}>
                  <WorkflowHomePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/red-dot/new"
            element={
              <ProtectedRoute requireWorkflow="red_dot">
                <Suspense fallback={null}>
                  <RedDotFormPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/red-dot/:id"
            element={
              <ProtectedRoute
                requireWorkflow="red_dot"
                workflowBypassPermissions={['action_center:full']}
              >
                <Suspense fallback={null}>
                  <RedDotDetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Preference Assessment CDSS */}
          <Route
            path="/preference-assessment"
            element={
              <ProtectedRoute requireWorkflow="preference_assessment">
                <Suspense fallback={null}>
                  <PAHomePage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/preference-assessment/new"
            element={
              <ProtectedRoute requireWorkflow="preference_assessment">
                <Suspense fallback={null}>
                  <PACdssPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/preference-assessment/cdss"
            element={
              <ProtectedRoute requireWorkflow="preference_assessment">
                <Suspense fallback={null}>
                  <PACdssPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/preference-assessment/:id"
            element={
              <ProtectedRoute requireWorkflow="preference_assessment">
                <Suspense fallback={null}>
                  <PADetailPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Client Management — both /clients and /clients/:id render the same list page.
              When :id is present, ClientManagementPage opens the ClientDetailDrawer overlay. */}
          {/* Action Center — the Org Admin inbox. Two stacked sections gated on
              separate permissions (`action_center:full` for Needs Assignment,
              `support_requests:manage` for the support queue), so the route
              admits either and the page decides which sections to render.
              `/action-center/support/:id` opens a support thread in place. */}
          <Route
            path="/action-center"
            element={
              <ProtectedRoute requiredPermissions={['action_center:full', 'support_requests:manage']}>
                <Suspense fallback={null}>
                  <ActionCenterPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          <Route
            path="/action-center/support/:id"
            element={
              <ProtectedRoute requiredPermissions={['support_requests:manage']}>
                <Suspense fallback={null}>
                  <ActionCenterPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
          {/* Legacy support-queue paths — the queue moved into the Action
              Center. Kept as redirects so deep links in already-sent emails and
              stored notifications still resolve. */}
          <Route path="/support/requests" element={<Navigate to="/action-center" replace />} />
          <Route
            path="/support/requests/:id"
            element={<LegacySupportRedirect to="/action-center/support" />}
          />
          <Route
            path="/clients"
            element={
              <ProtectedRoute requiredPermissions={['clients:manage']}>
                <ClientManagementPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clients/:id"
            element={
              <ProtectedRoute requiredPermissions={['clients:manage']}>
                <ClientManagementPage />
              </ProtectedRoute>
            }
          />
          {/* Analytics — Red Dot Insights dashboard (gated by red_dot:manage) */}
          <Route
            path="/analytics"
            element={
              <ProtectedRoute requiredPermissions={['red_dot:manage']}>
                <Suspense fallback={null}>
                  <AnalyticsPage />
                </Suspense>
              </ProtectedRoute>
            }
          />
        </Route>

        {/* Root redirect */}
        <Route
          path="/"
          element={
            isAuthenticated ? (
              <Navigate to={defaultRedirect} replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Catch all - redirect to home */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </>
  );
}

/* Styled components for the session warning modal */
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
`;

const ModalContent = styled.div`
  background: ${({ theme }) => theme.colors.backgrounds.white};
  border-radius: 8px;
  padding: 32px;
  max-width: 420px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 24px rgba(0, 0, 0, 0.2);
`;

const ModalTitle = styled.h2`
  margin: 0 0 16px;
  font-size: 20px;
  color: ${({ theme }) => theme.colors.status.error.text};
`;

const ModalText = styled.p`
  margin: 0 0 12px;
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  line-height: 1.5;
`;

const ModalButton = styled.button`
  margin-top: 8px;
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.white};
  background: ${({ theme }) => theme.colors.primary.base};
  border: none;
  border-radius: 4px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.primary.dark};
  }
`;

export default App;
